import * as fs from "fs/promises";
import * as path from "path";

import simpleGit from "simple-git";

import { GIT_CONSTANTS, METADATA_CONSTANTS } from "../constants";
import { atomicWriteFile } from "../utils/atomic-write";

import { Logger } from "./logger.service";

import type { SyncMetadata } from "../types/sync-metadata";

export class WorktreeMetadataService {
  private logger: Logger;

  constructor(logger?: Logger) {
    this.logger = logger ?? Logger.createDefault();
  }

  /**
   * Metadata is keyed by worktree path basename. Branch names are sanitized
   * into unique basenames before worktrees are created.
   */
  private getWorktreeDirectoryName(worktreePath: string): string {
    return path.basename(worktreePath);
  }

  async getMetadataPath(bareRepoPath: string, worktreeName: string): Promise<string> {
    if (worktreeName.includes("/") || worktreeName.includes("\\")) {
      throw new Error(
        `getMetadataPath requires a filesystem-safe worktree directory name, got '${worktreeName}'. Use getMetadataPathFromWorktreePath when starting from a raw branch name.`,
      );
    }
    return path.join(
      bareRepoPath,
      METADATA_CONSTANTS.WORKTREE_METADATA_PATH,
      worktreeName,
      METADATA_CONSTANTS.METADATA_FILENAME,
    );
  }

  async getMetadataPathFromWorktreePath(bareRepoPath: string, worktreePath: string): Promise<string> {
    // Extract the worktree directory name (basename) that Git actually uses
    const worktreeDirName = this.getWorktreeDirectoryName(worktreePath);
    return this.getMetadataPath(bareRepoPath, worktreeDirName);
  }

  async saveMetadata(bareRepoPath: string, worktreeName: string, metadata: SyncMetadata): Promise<boolean> {
    const metadataPath = await this.getMetadataPath(bareRepoPath, worktreeName);
    const existing = await this.loadMetadata(bareRepoPath, worktreeName);
    if (existing && (await this.validateMetadata(existing)) && existing.upstreamBranch !== metadata.upstreamBranch) {
      this.logger.warn(
        `Refusing to overwrite metadata for ${worktreeName}: existing upstream ${existing.upstreamBranch} differs from ${metadata.upstreamBranch}`,
      );
      return false;
    }
    await fs.mkdir(path.dirname(metadataPath), { recursive: true });
    await atomicWriteFile(metadataPath, JSON.stringify(metadata, null, 2));
    return true;
  }

  async loadMetadata(bareRepoPath: string, worktreeName: string): Promise<SyncMetadata | null> {
    const metadataPath = await this.getMetadataPath(bareRepoPath, worktreeName);

    try {
      const content = await fs.readFile(metadataPath, "utf-8");
      return JSON.parse(content) as SyncMetadata;
    } catch {
      // Return null if file doesn't exist or can't be parsed
      return null;
    }
  }

  async loadMetadataFromPath(bareRepoPath: string, worktreePath: string): Promise<SyncMetadata | null> {
    const metadataPath = await this.getMetadataPathFromWorktreePath(bareRepoPath, worktreePath);

    try {
      const content = await fs.readFile(metadataPath, "utf-8");
      const metadata = JSON.parse(content) as SyncMetadata;

      if (!(await this.validateMetadata(metadata))) {
        this.logger.warn(`Corrupted metadata for ${worktreePath}, treating as missing`);
        return null;
      }

      return metadata;
    } catch {
      return null;
    }
  }

  async deleteMetadata(bareRepoPath: string, worktreeName: string): Promise<void> {
    const metadataPath = await this.getMetadataPath(bareRepoPath, worktreeName);

    try {
      await fs.unlink(metadataPath);
    } catch (error) {
      // Ignore errors if file doesn't exist
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") {
        throw error;
      }
    }
  }

  async deleteMetadataFromPath(bareRepoPath: string, worktreePath: string): Promise<void> {
    const metadataPath = await this.getMetadataPathFromWorktreePath(bareRepoPath, worktreePath);

    try {
      await fs.unlink(metadataPath);
    } catch (error) {
      // Ignore errors if file doesn't exist
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") {
        throw error;
      }
    }
  }

  async updateLastSync(
    bareRepoPath: string,
    worktreeName: string,
    commit: string,
    action: "created" | "updated" | "fetched" = "updated",
  ): Promise<void> {
    const existing = await this.loadMetadata(bareRepoPath, worktreeName);

    if (!existing) {
      this.logger.warn(
        `No metadata found for worktree ${worktreeName}; skipping update because upstream/parent context is unavailable`,
      );
      return;
    }

    existing.lastSyncCommit = commit;
    existing.lastSyncDate = new Date().toISOString();

    existing.syncHistory.push({
      date: existing.lastSyncDate,
      commit,
      action,
    });

    if (existing.syncHistory.length > METADATA_CONSTANTS.MAX_HISTORY_ENTRIES) {
      existing.syncHistory = existing.syncHistory.slice(-METADATA_CONSTANTS.MAX_HISTORY_ENTRIES);
    }

    await this.saveMetadata(bareRepoPath, worktreeName, existing);
  }

  async updateLastSyncFromPath(
    bareRepoPath: string,
    worktreePath: string,
    commit: string,
    action: "created" | "updated" | "fetched" = "updated",
    defaultBranch?: string,
  ): Promise<void> {
    const worktreeDirName = this.getWorktreeDirectoryName(worktreePath);
    const existing = await this.loadMetadataFromPath(bareRepoPath, worktreePath);

    if (!existing) {
      this.logger.warn(`No metadata found for worktree ${worktreeDirName}`);
      this.logger.info(`  Attempting to create initial metadata...`);

      try {
        const worktreeGit = simpleGit(worktreePath);
        const currentCommit = await worktreeGit.revparse(["HEAD"]);

        const branchSummary = await worktreeGit.branch();
        const actualBranchName = branchSummary.current;

        if (!actualBranchName) {
          throw new Error("Could not determine current branch name");
        }

        let upstreamBranch = `origin/${actualBranchName}`;
        try {
          const configuredUpstream = await worktreeGit.raw([
            "rev-parse",
            "--abbrev-ref",
            `${actualBranchName}@{upstream}`,
          ]);
          if (configuredUpstream.trim()) {
            upstreamBranch = configuredUpstream.trim();
          }
        } catch {
          // No configured upstream, use constructed value
        }

        const parentBranch = defaultBranch || GIT_CONSTANTS.DEFAULT_BRANCH;

        const created = await this.createInitialMetadataFromPath(
          bareRepoPath,
          worktreePath,
          currentCommit.trim(),
          upstreamBranch,
          parentBranch,
          currentCommit.trim(),
        );
        if (created) {
          this.logger.info(`  ✅ Created metadata for ${worktreeDirName}`);
        } else {
          this.logger.warn(`  ⚠️ Metadata for ${worktreeDirName} was not overwritten`);
        }
        return;
      } catch (error) {
        this.logger.error(`  ❌ Failed to create metadata`, error);
        throw error;
      }
    }

    // Update metadata
    existing.lastSyncCommit = commit;
    existing.lastSyncDate = new Date().toISOString();

    existing.syncHistory.push({
      date: existing.lastSyncDate,
      commit,
      action,
    });

    if (existing.syncHistory.length > METADATA_CONSTANTS.MAX_HISTORY_ENTRIES) {
      existing.syncHistory = existing.syncHistory.slice(-METADATA_CONSTANTS.MAX_HISTORY_ENTRIES);
    }

    // Save using the directory name
    await this.saveMetadata(bareRepoPath, worktreeDirName, existing);
  }

  /**
   * Records the upstream tip observed during this sync. This is what later
   * proves "HEAD was fully pushed" after the remote branch is deleted, so it
   * must only ever be overwritten with a live observation — callers must not
   * invoke this once the upstream ref is gone.
   */
  async recordRemoteTip(bareRepoPath: string, worktreePath: string, ref: string, oid: string): Promise<void> {
    const worktreeDirName = this.getWorktreeDirectoryName(worktreePath);
    const existing = await this.loadMetadataFromPath(bareRepoPath, worktreePath);

    if (!existing) {
      // debug, not warn: this runs every sync tick for every worktree, and a
      // legacy worktree without metadata would otherwise spam the log forever.
      this.logger.debug(`No metadata found for worktree ${worktreeDirName}; skipping remote tip recording`);
      return;
    }

    if (existing.lastKnownRemoteTip?.ref === ref && existing.lastKnownRemoteTip.oid === oid) {
      return;
    }

    existing.lastKnownRemoteTip = { ref, oid, recordedAt: new Date().toISOString() };
    await this.saveMetadata(bareRepoPath, worktreeDirName, existing);
  }

  async createInitialMetadata(
    bareRepoPath: string,
    worktreeName: string,
    commit: string,
    upstreamBranch: string,
    parentBranch: string,
    parentCommit: string,
  ): Promise<boolean> {
    const metadata: SyncMetadata = {
      lastSyncCommit: commit,
      lastSyncDate: new Date().toISOString(),
      upstreamBranch,
      createdFrom: {
        branch: parentBranch,
        commit: parentCommit,
      },
      syncHistory: [
        {
          date: new Date().toISOString(),
          commit,
          action: "created",
        },
      ],
    };

    return this.saveMetadata(bareRepoPath, worktreeName, metadata);
  }

  async createInitialMetadataFromPath(
    bareRepoPath: string,
    worktreePath: string,
    commit: string,
    upstreamBranch: string,
    parentBranch: string,
    parentCommit: string,
  ): Promise<boolean> {
    const worktreeDirName = this.getWorktreeDirectoryName(worktreePath);
    const metadata: SyncMetadata = {
      lastSyncCommit: commit,
      lastSyncDate: new Date().toISOString(),
      upstreamBranch,
      createdFrom: {
        branch: parentBranch,
        commit: parentCommit,
      },
      syncHistory: [
        {
          date: new Date().toISOString(),
          commit,
          action: "created",
        },
      ],
    };

    return this.saveMetadata(bareRepoPath, worktreeDirName, metadata);
  }

  async validateMetadata(metadata: SyncMetadata): Promise<boolean> {
    if (!metadata.lastSyncCommit || !metadata.lastSyncDate || !metadata.upstreamBranch) {
      return false;
    }

    if (!/^[0-9a-f]+$/i.test(metadata.lastSyncCommit)) {
      return false;
    }

    if (Number.isNaN(new Date(metadata.lastSyncDate).getTime())) {
      return false;
    }

    return true;
  }
}
